'use client';
import React, { FC } from 'react';
import AmplienceDynamicComponent from '../dynamic-component';

export interface AmplienceLayoutProps {
  numberOfColumns: number;
  components: [any];
  itemsGap: number;
  firstColumnWidth?: number;
  secondColumnWidth?: number;
  thirdColumnWidth?: number;
  fourthColumnWidth?: number;
}

const AmplienceLayout: FC<AmplienceLayoutProps> = ({
  components,
  numberOfColumns,
  itemsGap = 16,
  firstColumnWidth,
  secondColumnWidth,
  thirdColumnWidth,
  fourthColumnWidth,
}) => {
  const colsVariants: { [key: number]: string } = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  };

  const getGridTemplateColumns = () => {
    const widths = [firstColumnWidth, secondColumnWidth, thirdColumnWidth, fourthColumnWidth].slice(0, numberOfColumns);

    // Sum of the total defined values e.g (1st width: 30 , 3rd width: 50) -> 80
    const totalDefined = widths.reduce((acc: number, val) => acc + (val ?? 0), 0);

    const remaining = 100 - totalDefined;

    // Get the total of undefined widhts based on validWidths
    const undefinedCount = widths.filter((w) => w === undefined).length;

    // Distribute the existing width column value to corresponding position, then the rest set the remaining / undefinedCount
    // e.g 3 columns, 2do column width -> 50, and 1st and 3rd are undefined -> it will return an array [25,50,25]
    const distributed = widths.map((w) => {
      if (w !== undefined) return w;
      return remaining / undefinedCount;
    });

    // Convert the distributed values into 'fr' units, which are used in CSS Grid for flexible column widths.
    // We use `minmax(0, ...)` to ensure the columns stretch within the available space without overflowing.
    const frValues = distributed.map((w) => `minmax(0, ${(w / 100).toFixed(3)}fr)`);

    // Convert the array of 'fr' values into a string to use in the 'grid-template-columns' property.
    return frValues.join(' ');
  };

  const layoutClass = numberOfColumns === 4 ? 'layout-4-col' : 'layout-container';

  return (
    <div
      style={{
        gap: `${itemsGap}px`,
        gridTemplateColumns: `${getGridTemplateColumns()}`,
      }}
      className={`${colsVariants[numberOfColumns]} ${layoutClass} mx-auto grid max-w-[--page-max-width] grid-cols-1`}
    >
      {components?.map((component, index) => {
        return (
          <AmplienceDynamicComponent
            key={'layout-' + numberOfColumns + '-component0' + index}
            data={{ ...component, numberOfColumns: numberOfColumns }}
          />
        );
      })}
    </div>
  );
};

export default AmplienceLayout;
